import {
  Button,
  Popover,
  HStack,
  Text,
  Spacer,
  Stack,
  IconButton,
  StackSeparator,
  useDisclosure,
  Box,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { CampaignManagementModal } from "./CampaignManagementModal";
import { t } from "i18next";
import { useStores } from "../../../../shared/stores/RootStore";
import {
  AddIcon,
  CheckIcon,
  ChevronDownIcon,
  EditIcon,
} from "../../../../shared/views/icons";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { toast } from "../../../../shared/views/CustomToaster";

const i18prefix = "home:campaignSelection";

const CampaignSelectionBase: React.FC = () => {
  const { campaignStore, projectStore } = useStores();
  const popoverState = useDisclosure();
  const campaignManagementModal = useDisclosure();
  const selectedCampaign = campaignStore.selected;
  const selectedProject = projectStore.getSelected();

  return (
    <>
      <CustomPopover
        open={popoverState.open}
        onOpen={() => {
          popoverState.onOpen();
        }}
        onClose={popoverState.onClose}
        trigger={
          <Button
            size="sm"
            colorPalette="primary"
            variant="ghost"
            fontSize="sm"
            disabled={!selectedProject}
          >
            {selectedCampaign
              ? selectedCampaign.name
              : t(`${i18prefix}.defaultButton`)}
            <ChevronDownIcon />
          </Button>
        }
      >
        <Popover.Header>
          <HStack>
            <Text fontWeight={"semibold"}>{t(`${i18prefix}.title`)}</Text>
            <Spacer />
            <Button
              m={-1}
              colorPalette="primary"
              variant={"ghost"}
              size="sm"
              disabled={!selectedProject?.permissions.canCreate}
              onClick={() => {
                const date = new Date();
                campaignStore
                  .create({
                    projectId: selectedProject!.id,
                    name: `${date.getFullYear()}`,
                  })
                  .then(() => {
                    toast("success", "Campaign created");
                  });
              }}
            >
              <AddIcon /> {t(`${i18prefix}.create`)}
            </Button>
          </HStack>
        </Popover.Header>
        <Popover.Body px={0} pt={2}>
          <Stack gap={0} separator={<StackSeparator />}>
            {campaignStore.campaigns.map((campaign) => {
              const createdAt = new Date(campaign.createdAt);
              return (
                <HStack gap={0} key={campaign.id}>
                  <CampaignItem
                    onClick={() => campaignStore.setSelected(campaign.id)}
                    selected={campaign === selectedCampaign}
                    name={campaign.name}
                    description={t(`${i18prefix}.createdAt`, {
                      date: createdAt.toDateString(),
                    })}
                  />
                  {campaign === selectedCampaign && (
                    <IconButton
                      colorPalette="primary"
                      variant="ghost"
                      onClick={() => {
                        popoverState.onClose();
                        campaignManagementModal.onOpen();
                      }}
                      mr={2}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                </HStack>
              );
            })}
          </Stack>
        </Popover.Body>
      </CustomPopover>

      {campaignManagementModal.open && (
        <CampaignManagementModal onClose={campaignManagementModal.onClose} />
      )}
    </>
  );
};

export const CampaignSelection = observer(CampaignSelectionBase);

interface ItemProps {
  onClick: () => void;
  name: string;
  description: string;
  selected: boolean;
}
const CampaignItem: React.FC<ItemProps> = ({
  selected,
  name,
  description,
  onClick,
}) => {
  return (
    <HStack
      alignItems="stretch"
      gap={0}
      separator={<StackSeparator />}
      flexGrow={1}
    >
      <HStack
        flexGrow={1}
        _hover={{ textDecoration: "underline" }}
        cursor="pointer"
        px={3}
        py={2}
        onClick={onClick}
      >
        <Box w="20px">{selected && <CheckIcon color="primary.600" />}</Box>
        <Stack gap={0}>
          <Text fontSize="sm">{name}</Text>
          <Text fontSize="xs">{description}</Text>
        </Stack>
      </HStack>
    </HStack>
  );
};
